import { css, cx } from "@emotion/css";
import { Badge } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { type SyntheticEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CSSTransition } from "react-transition-group";
import { QuestionsAndAnswers } from "src/components/Main/News/Forum/QuestionsAndAnswers";
import { LastComments } from "src/components/Main/News/LastCommentedQuestions/LastComments";
import { useForumCommentCount } from "src/hooks/useComments";
import useLocalStorage from "src/hooks/useLocalStorage";
import ArrowDownIcon from "src/images/Icons/newIcons/arrowDown.svg";
import ArrowUpIcon from "src/images/Icons/newIcons/arrowUp.svg";
import LastCommentsIcon from "src/images/Icons/newIcons/lastComments.svg";

import { Tooltip } from "../../Default/LekUI/Tooltip";
import Box from "../../Default/Main/Box";

export const ActionContainerId = "news-action-container-id";
export const SearchContainerId = "news-search-container-id";

interface NewsProps {
	className?: string;
}

export function News({ className }: NewsProps) {
	const { isLoading, data: count } = useForumCommentCount();
	const [selectedTab, setSelectedTab] = useState(0);
	const [isOpen, setIsOpen] = useLocalStorage("last-comments-open", true);
	const Icon = isOpen ? ArrowUpIcon : ArrowDownIcon;
	const { t } = useTranslation();
	const ref = useRef<HTMLDivElement>(null);

	const toggleOpen = () => {
		setIsOpen(!isOpen);
	};

	return (
		<Box
			className="last_commented_questions_container"
			headerClassName="mb-0"
			Header={
				<div
					className={cx(
						className,
						"last_commented_questions_container--header",
						{
							"last_commented_questions_container--header--hidden": !isOpen,
						},
					)}
				>
					<div className="flex-center-row gap-8 mt-4">
						<LastCommentsIcon className="icon-normal" />
						<h2 className="size-18 m-0">{t("commentedQuestions.title")}</h2>
					</div>
					<div className="flex gap-12">
						<div id={SearchContainerId} />
						<Tooltip title={t(`commands.${isOpen ? "collapse" : "expand"}`)}>
							<IconButton
								size="small"
								className="menu-icon-button"
								onClick={toggleOpen}
								disableRipple
							>
								<Icon className="icon-normal icon-black" />
							</IconButton>
						</Tooltip>
					</div>
				</div>
			}
		>
			<CSSTransition
				in={isOpen}
				timeout={200}
				classNames="collapse"
				unmountOnExit
				nodeRef={ref}
			>
				<div
					ref={ref}
					className={cx("last_commented_questions", {
						"mt-16": isOpen,
					})}
				>
					<div className="flex items-center justify-between">
						<Tabs
							value={selectedTab}
							textColor="primary"
							onChange={(_event: SyntheticEvent, newValue: number) => setSelectedTab(newValue)}
							variant="scrollable"
							className="tabs"
						>
							<Tab
								className="tab size-16 p-relative"
								label={
									<span className="first-letter-upper">
										{t("news.tabs.lastCommented")}
									</span>
								}
								key="lastComments"
							/>
							<Tab
								className="tab size-16 p-relative"
								label={
									<Badge
										color="secondary"
										variant="dot"
										invisible={isLoading || count === 0}
										className={cx(
											"badge-right-top",
											css`
                        > .MuiBadge-badge {
                          right: -4px;
                        }
                      `,
										)}
									>
										<span className="first-letter-upper">
											{t("news.tabs.community")}
										</span>
									</Badge>
								}
								key="community"
							/>
						</Tabs>
						<div id={ActionContainerId} />
					</div>
					{selectedTab === 0 && (
						<LastComments isOpen={isOpen && selectedTab === 0} />
					)}
					{selectedTab === 1 && (
						<QuestionsAndAnswers isOpen={isOpen && selectedTab === 1} />
					)}
				</div>
			</CSSTransition>
		</Box>
	);
}
